"use client";

import {
  ActivityIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  FilterIcon,
  SearchIcon,
} from "lucide-react";
import { memo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getEventName } from "@/lib/blockchain/event-signatures";
import { cn } from "@/lib/utils";
import { AddressDisplay } from "./address-display";
import { ChainBadge } from "./chain-badge";
import { CollapsibleCard } from "./collapsible-card";
import { QuickActionsBar } from "./quick-actions-bar";
import type { EventLogEntry, EventLogsProps, QuickAction } from "./types";
import { getChainInfo } from "./types";

/**
 * Default number of logs to show initially
 */
const DEFAULT_VISIBLE_COUNT = 10;

/**
 * Truncate hex string for display
 */
function truncateHex(hex: string, chars = 8): string {
  if (hex.length <= chars * 2 + 4) {
    return hex;
  }
  return `${hex.slice(0, chars + 2)}...${hex.slice(-chars)}`;
}

/**
 * Format timestamp to human readable
 */
function formatTimestamp(timestamp: string): string {
  const ts = Number(timestamp);
  if (Number.isNaN(ts)) {
    return timestamp;
  }
  const date = new Date(ts * 1000);
  return date.toLocaleString();
}

/**
 * Copy text to clipboard
 */
async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

/**
 * Main EventLogs component - displays event logs from smart contracts
 */
const PureEventLogs = ({
  cardId,
  input,
  output,
  state,
  onQuickAction,
}: EventLogsProps) => {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);
  const chain = getChainInfo(input.chainId);

  // Handle loading states
  if (state === "input-streaming" || state === "input-available") {
    return (
      <EventLogsSkeleton
        address={input.address}
        chainId={input.chainId}
        topic0={input.topic0}
      />
    );
  }

  // Handle error state
  if (output && "error" in output) {
    return (
      <EventLogsError
        address={input.address}
        chainId={input.chainId}
        error={output.error}
      />
    );
  }

  // Handle empty output
  if (!output || output.logs.length === 0) {
    return (
      <EventLogsEmpty
        address={input.address}
        blockRange={
          output?.blockRange ?? { from: input.fromBlock, to: input.toBlock }
        }
        chainId={input.chainId}
        onQuickAction={onQuickAction}
        topic0={input.topic0}
      />
    );
  }

  const logs = output.logs;
  const visibleLogs = logs.slice(0, visibleCount);
  const hasMore = logs.length > visibleCount;

  // Generate quick actions
  const quickActions: QuickAction[] = [
    {
      label: "Analyze events",
      icon: <SearchIcon className="size-3" />,
      prompt: input.address
        ? `Analyze the event patterns for contract ${input.address} on ${chain.name}. What can we learn from these ${output.count} events?`
        : `Analyze these ${output.count} event logs. What patterns do you see?`,
    },
    {
      label: "Decode data",
      icon: <FilterIcon className="size-3" />,
      prompt:
        "Can you help me decode the data field from some of these events? What information is stored in the non-indexed data?",
    },
  ];

  // Add address-specific actions if we have an address
  if (input.address) {
    quickActions.push({
      label: "Get contract info",
      icon: <ActivityIcon className="size-3" />,
      prompt: `Tell me more about the contract at ${input.address} on ${chain.name}. What does it do?`,
    });
  }

  // Determine event name/label for header badge
  const eventName = input.topic0 ? getEventName(input.topic0) : null;
  const isKnownEvent = eventName && eventName !== "Unknown Event";
  const eventLabel = isKnownEvent
    ? eventName
    : input.topic0
      ? truncateHex(input.topic0, 6)
      : null;

  // Header content (always visible when collapsed)
  const headerContent = (
    <div
      className={cn(
        "flex items-center justify-between border-b bg-gradient-to-br p-4 pr-12",
        chain.bgGradient
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 items-center justify-center rounded-full"
          style={{ backgroundColor: `${chain.color}20` }}
        >
          <ActivityIcon className="size-5" style={{ color: chain.color }} />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2 font-medium">
            <span>{output.count} event logs</span>
            {eventLabel && (
              <Badge
                className={cn(
                  "flex items-center gap-1 text-xs",
                  !isKnownEvent && "font-mono"
                )}
                variant="secondary"
              >
                <FilterIcon className="size-3" />
                {eventLabel}
              </Badge>
            )}
            <ChainBadge chainId={input.chainId} size="sm" />
          </div>
          {input.address && (
            <div className="flex items-center gap-1 text-muted-foreground text-xs">
              <AddressDisplay
                address={input.address}
                chainId={input.chainId}
                showCopy={false}
                showExplorerLink={false}
                size="sm"
              />
            </div>
          )}
        </div>
      </div>

      <div className="text-right text-muted-foreground text-xs">
        <div>
          Block {output.blockRange.from} → {output.blockRange.to}
        </div>
        {output.message && (
          <div className="mt-1 text-amber-600 dark:text-amber-400">
            {output.truncated ? "Warning: " : ""}
            {output.message}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <CollapsibleCard cardId={cardId} headerContent={headerContent}>
      {/* Event logs list */}
      <div className="divide-y bg-background">
        {visibleLogs.map((log) => (
          <EventLogRow
            chainId={input.chainId}
            key={`${log.transactionHash}-${log.logIndex}`}
            log={log}
          />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="border-t bg-muted/30 p-3 text-center">
          <button
            className="text-primary text-sm hover:underline"
            onClick={() =>
              setVisibleCount((prev) =>
                Math.min(prev + DEFAULT_VISIBLE_COUNT, logs.length)
              )
            }
            type="button"
          >
            Show more ({logs.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {/* Quick actions */}
      <div className="border-t bg-muted/20 p-4">
        <QuickActionsBar
          actions={quickActions}
          maxVisible={3}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </CollapsibleCard>
  );
};

export const EventLogs = memo(PureEventLogs);

/**
 * Single event log row component
 */
function EventLogRow({
  log,
  chainId,
}: {
  log: EventLogEntry;
  chainId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const chain = getChainInfo(chainId);
  const eventName = getEventName(log.topics[0]);

  return (
    <div className="p-3">
      {/* Main row */}
      <div className="flex items-center gap-3">
        <button
          className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted"
          onClick={() => setExpanded(!expanded)}
          type="button"
        >
          {expanded ? (
            <ChevronDownIcon className="size-4" />
          ) : (
            <ChevronRightIcon className="size-4" />
          )}
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Event name badge */}
          <span className="shrink-0 rounded bg-primary/10 px-2 py-0.5 font-mono text-primary text-xs">
            {eventName}
          </span>

          {/* Contract address */}
          <AddressDisplay
            address={log.address}
            chainId={chainId}
            showCopy={false}
            showExplorerLink={false}
            size="sm"
          />

          {/* Block number */}
          <span className="shrink-0 text-muted-foreground text-xs">
            Block {log.blockNumber}
          </span>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1">
          <a
            className="flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            href={`${chain.explorerUrl}/tx/${log.transactionHash}`}
            rel="noopener noreferrer"
            target="_blank"
            title="View transaction"
          >
            <ExternalLinkIcon className="size-3.5" />
          </a>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-3 ml-9 space-y-2 rounded bg-muted/30 p-3 font-mono text-xs">
          {/* Transaction hash */}
          <div className="flex items-start gap-2">
            <span className="shrink-0 text-muted-foreground">tx:</span>
            <span className="break-all">{log.transactionHash}</span>
            <button
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => copyToClipboard(log.transactionHash)}
              title="Copy transaction hash"
              type="button"
            >
              <CopyIcon className="size-3" />
            </button>
          </div>

          {/* Topics */}
          {log.topics.map((topic, i) => (
            <div className="flex items-start gap-2" key={`topic-${i}-${topic}`}>
              <span className="shrink-0 text-muted-foreground">topic{i}:</span>
              <span className="break-all">{topic}</span>
              <button
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => copyToClipboard(topic)}
                title={`Copy topic${i}`}
                type="button"
              >
                <CopyIcon className="size-3" />
              </button>
            </div>
          ))}

          {/* Data */}
          {log.data && log.data !== "0x" && (
            <div className="flex items-start gap-2">
              <span className="shrink-0 text-muted-foreground">data:</span>
              <span className="break-all">{truncateHex(log.data, 32)}</span>
              <button
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => copyToClipboard(log.data)}
                title="Copy data"
                type="button"
              >
                <CopyIcon className="size-3" />
              </button>
            </div>
          )}

          {/* Timestamp */}
          {log.timestamp && (
            <div className="flex items-start gap-2">
              <span className="shrink-0 text-muted-foreground">time:</span>
              <span>{formatTimestamp(log.timestamp)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton loader for event logs
 */
function EventLogsSkeleton({
  address,
  chainId,
  topic0,
}: {
  address?: string;
  chainId: string;
  topic0?: string;
}) {
  const chain = getChainInfo(chainId);

  return (
    <div className="overflow-hidden rounded-xl border">
      {/* Header skeleton */}
      <div
        className={cn(
          "flex items-center justify-between border-b bg-gradient-to-br p-4",
          chain.bgGradient
        )}
      >
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-32" />
              <ChainBadge chainId={chainId} size="sm" />
            </div>
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-8 w-32" />
      </div>

      {/* Loading indicator */}
      <div className="flex items-center justify-center gap-2 border-b bg-background p-4">
        <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-muted-foreground text-sm">
          Scanning blocks for event logs...
        </span>
      </div>

      {/* Skeleton rows */}
      <div className="divide-y bg-background">
        {Array.from({ length: 5 }).map((_, i) => (
          <div className="flex items-center gap-3 p-3" key={i}>
            <Skeleton className="size-6" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Empty state for event logs
 */
function EventLogsEmpty({
  address,
  chainId,
  blockRange,
  topic0,
  onQuickAction,
}: {
  address?: string;
  chainId: string;
  blockRange: { from: string; to: string };
  topic0?: string;
  onQuickAction: EventLogsProps["onQuickAction"];
}) {
  const chain = getChainInfo(chainId);
  const eventName = getEventName(topic0);

  const quickActions: QuickAction[] = [
    {
      label: "Try different block range",
      icon: <SearchIcon className="size-3" />,
      prompt: address
        ? `Search for event logs from contract ${address} on ${chain.name} in a different block range. Try the last 10,000 blocks.`
        : `Search for ${eventName} events on ${chain.name} in a different block range.`,
    },
  ];

  if (address) {
    quickActions.push({
      label: "Get contract transactions",
      icon: <ActivityIcon className="size-3" />,
      prompt: `Show me the recent transactions for contract ${address} on ${chain.name}.`,
    });
  }

  return (
    <div className="overflow-hidden rounded-xl border">
      <div
        className={cn(
          "flex items-center justify-between border-b bg-gradient-to-br p-4",
          chain.bgGradient
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex size-10 items-center justify-center rounded-full"
            style={{ backgroundColor: `${chain.color}20` }}
          >
            <ActivityIcon className="size-5" style={{ color: chain.color }} />
          </div>
          <div>
            <div className="flex items-center gap-2 font-medium">
              <span>No event logs found</span>
              <ChainBadge chainId={chainId} size="sm" />
            </div>
            {address && (
              <AddressDisplay
                address={address}
                chainId={chainId}
                showCopy={false}
                showExplorerLink
                size="sm"
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-background p-6 text-center">
        <div className="mb-2 text-muted-foreground">
          No event logs found matching the specified criteria
        </div>
        <div className="text-muted-foreground/70 text-xs">
          Block range: {blockRange.from} → {blockRange.to}
          {topic0 && <span className="ml-2">Event: {eventName}</span>}
        </div>
      </div>

      <div className="border-t bg-muted/20 p-4">
        <QuickActionsBar
          actions={quickActions}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </div>
  );
}

/**
 * Error state for event logs
 */
function EventLogsError({
  error,
  address,
  chainId,
}: {
  error: string;
  address?: string;
  chainId: string;
}) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
          <ActivityIcon className="size-4 text-destructive" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-destructive">
            Failed to retrieve event logs
          </h4>
          <p className="mt-1 text-destructive/80 text-sm">{error}</p>
          {address && (
            <div className="mt-2 text-destructive/70 text-xs">
              Contract: {address}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
